'use client'

import { FilterCard } from '@/app/[locale]/collection/_components/filter-card'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { cn } from '@/lib/utils'
import { TAttributeCardItem, TCollectionFormatted } from '@/types/collection'
import { useTranslations } from 'next-intl'
import { sortedAttributeCardsUnderwear } from '../../constants'
import { useFilterState } from '../filter-bar/use-filter-state'

interface FilterCarouselProp {
  collection: TCollectionFormatted
  className?: string
  title?: string
}

export const FilterCarousel = ({
  collection,
  className,
  title,
}: FilterCarouselProp) => {
  const { changeFilter } = useFilterState({
    onClose: undefined,
    collection,
  })
  const t = useTranslations()

  // Check if attributeCards exists and has items
  const hasAttributeCards =
    collection.filter.attributeCards &&
    collection.filter.attributeCards.length > 0 &&
    collection.filter.attributeCards[0]?.items &&
    collection.filter.attributeCards[0].items.length > 0

  if (!hasAttributeCards) {
    return null
  }

  const sortedAttributeCards =
    collection.filter.attributeCards?.[0]?.items?.sort(
      (a: TAttributeCardItem, b: TAttributeCardItem) =>
        (a.sort ?? 0) - (b.sort ?? 0)
    )

  const isUnderWare = collection.filter.isUnderwearCollection
  const filterKey = collection.filter.attributeCards?.[0]?.seo_alias

  const allFilterCardItems = isUnderWare
    ? sortedAttributeCardsUnderwear.map((item) => ({
        ...item,
        title: t(item.title),
        sub_title: t(item.sub_title),
      }))
    : sortedAttributeCards || []

  // Filter out items that don't have valid images
  const filterCardItems = allFilterCardItems.filter(
    (item) => item.image && item.image.trim() !== ''
  )

  const finalFilterKey = isUnderWare ? 'kieu-dang-quan-lot' : filterKey

  if (!finalFilterKey || filterCardItems.length === 0) {
    return null
  }

  const onChangeFilter = (value: string) => {
    changeFilter(finalFilterKey, value)
  }

  const hasConcatActive = (value: string) => {
    const att = collection?.request?.att
    if (att && typeof att === 'object' && !Array.isArray(att)) {
      return att[finalFilterKey] === value
    }
    return false
  }

  const hasConcatParent =
    !Array.isArray(collection?.request?.att) &&
    filterCardItems.some((item) => hasConcatActive(item.seo_alias))

  return (
    <>
      {title && (
        <h1 className='mb-2 text-center font-criteria text-lg font-medium uppercase md:mb-4 md:text-2xl'>
          {title}
        </h1>
      )}
      <div className='relative' data-tracking-level-2={'FilterCarousel'}>
        <Carousel
          opts={{
            align: 'start',
            duration: 50,
            dragFree: true,
            skipSnaps: true,
          }}
          className='w-full'
        >
          <CarouselContent
            className={cn(className, {
              'lg:justify-center': filterCardItems.length < 6,
            })}
          >
            {filterCardItems.map((item, index) => (
              <CarouselItem
                key={index}
                className='xl:1/6 basis-2/5 will-change-transform md:basis-1/4 lg:basis-1/5 xl:basis-1/6 2xl:basis-[14.2%]'
              >
                <FilterCard
                  title={item.title}
                  description={item.short_description || item.sub_title || ''}
                  // TODO: get product count from api
                  // productCount={}
                  imageUrl={item.image}
                  imageHoverUrl={item?.image_hover || ''}
                  filterValue={item.seo_alias}
                  filterKey={finalFilterKey}
                  isUnderWare={isUnderWare}
                  onChangeFilter={onChangeFilter}
                  hasConcatActive={hasConcatActive(item.seo_alias)}
                  hasConcatParent={hasConcatParent}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselNavigation />
        </Carousel>
      </div>
    </>
  )
}

function CarouselNavigation() {
  return (
    <>
      <CarouselPrevious
        className='absolute -left-4 top-1/2 h-10 w-10 -translate-y-1/2 lg:-left-10'
        variant='black'
        hideIfStart
      />
      <CarouselNext
        className='absolute -right-4 top-1/2 h-10 w-10 -translate-y-1/2 lg:-right-10'
        variant='black'
        hideIfEnd
      />
    </>
  )
}
